(() => {
  const selector = [
    '.product-card-wrapper .card__media .media > img:first-of-type',
    '.gf-mix-card__media > img.gf-mix-card__image'
  ].join(',');

  if (!document.querySelector(selector)) {
    const once = new MutationObserver((mutations, observer) => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        init();
      }
    });
    once.observe(document.documentElement, { childList: true, subtree: true });
    return;
  }

  init();

  function init() {
    const cache = new Map();
    const queued = new WeakSet();

    const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

    function analyze(src) {
      if (cache.has(src)) return cache.get(src);

      const task = new Promise((resolve) => {
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.decoding = 'async';

        probe.onload = () => {
          try {
            const maxDim = 96;
            const ratio = probe.naturalWidth / Math.max(1, probe.naturalHeight);
            const w = ratio >= 1 ? maxDim : Math.max(32, Math.round(maxDim * ratio));
            const h = ratio >= 1 ? Math.max(32, Math.round(maxDim / ratio)) : maxDim;

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) return resolve(null);

            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(probe, 0, 0, w, h);
            const data = ctx.getImageData(0, 0, w, h).data;

            const border = Math.max(2, Math.round(Math.min(w, h) * 0.06));
            let edgeCount = 0;
            let transparentEdges = 0;
            let opaqueEdges = 0;
            let sr = 0, sg = 0, sb = 0;

            for (let y = 0; y < h; y++) {
              for (let x = 0; x < w; x++) {
                if (x >= border && x < w - border && y >= border && y < h - border) continue;
                const i = (y * w + x) * 4;
                const a = data[i + 3];
                edgeCount++;
                if (a < 35) {
                  transparentEdges++;
                } else {
                  opaqueEdges++;
                  sr += data[i];
                  sg += data[i + 1];
                  sb += data[i + 2];
                }
              }
            }

            const transparentBackground = transparentEdges / Math.max(1, edgeCount) > 0.55;
            const br = opaqueEdges ? sr / opaqueEdges : 255;
            const bg = opaqueEdges ? sg / opaqueEdges : 255;
            const bb = opaqueEdges ? sb / opaqueEdges : 255;
            const bgLum = (br + bg + bb) / 3;

            let closeEdges = 0;
            if (!transparentBackground && opaqueEdges) {
              for (let y = 0; y < h; y++) {
                for (let x = 0; x < w; x++) {
                  if (x >= border && x < w - border && y >= border && y < h - border) continue;
                  const i = (y * w + x) * 4;
                  if (data[i + 3] < 35) continue;
                  const dr = data[i] - br;
                  const dg = data[i + 1] - bg;
                  const db = data[i + 2] - bb;
                  if (Math.sqrt(dr * dr + dg * dg + db * db) < 16) closeEdges++;
                }
              }
            }

            const studioWhite =
              transparentBackground ||
              (bgLum > 235 && closeEdges / Math.max(1, opaqueEdges) > 0.72);

            if (!studioWhite) return resolve(null);

            let minX = w, minY = h, maxX = -1, maxY = -1, fg = 0;

            for (let y = 0; y < h; y++) {
              for (let x = 0; x < w; x++) {
                const i = (y * w + x) * 4;
                const a = data[i + 3];
                let isForeground = false;

                if (transparentBackground) {
                  isForeground = a > 45;
                } else if (a > 45) {
                  const dr = data[i] - br;
                  const dg = data[i + 1] - bg;
                  const db = data[i + 2] - bb;
                  const dist = Math.sqrt(dr * dr + dg * dg + db * db);
                  isForeground = dist > 20 || Math.min(data[i], data[i + 1], data[i + 2]) < 232;
                }

                if (!isForeground) continue;
                fg++;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }

            if (fg < w * h * 0.012 || maxX < minX || maxY < minY) return resolve(null);

            // Add a small visual safety margin around the detected packshot.
            const padX = Math.max(1, Math.round(w * 0.018));
            const padY = Math.max(1, Math.round(h * 0.018));
            minX = Math.max(0, minX - padX);
            maxX = Math.min(w - 1, maxX + padX);
            minY = Math.max(0, minY - padY);
            maxY = Math.min(h - 1, maxY + padY);

            const fw = (maxX - minX + 1) / w;
            const fh = (maxY - minY + 1) / h;
            const cx = (minX + maxX + 1) / (2 * w);
            const cy = (minY + maxY + 1) / (2 * h);

            if (fw > 0.97 && fh > 0.97) return resolve(null);

            const target = 0.82;
            let scale = Math.min(target / Math.max(fw, 0.01), target / Math.max(fh, 0.01));
            scale = clamp(scale, 0.90, 1.34);

            // Ignore tiny corrections to avoid needless style churn.
            const dx = clamp((0.5 - cx) * 100, -7, 7);
            const dy = clamp((0.5 - cy) * 100, -7, 7);
            if (Math.abs(scale - 1) < 0.035 && Math.abs(dx) < 1.2 && Math.abs(dy) < 1.2) {
              return resolve({ scale: 1, dx: 0, dy: 0 });
            }

            resolve({ scale, dx, dy });
          } catch (_) {
            resolve(null);
          }
        };

        probe.onerror = () => resolve(null);
        probe.src = src;
      });

      cache.set(src, task);
      return task;
    }

    async function normalize(img) {
      if (!img || queued.has(img)) return;
      queued.add(img);

      if (!img.complete) {
        img.addEventListener('load', () => normalizeReady(img), { once: true });
      } else {
        normalizeReady(img);
      }
    }

    async function normalizeReady(img) {
      const src = img.currentSrc || img.src;
      if (!src) return;

      const result = await analyze(src);
      if (!result) {
        img.dataset.gfFeaturedSizing = 'skip';
        return;
      }

      img.classList.add('gf-featured-auto-size');
      img.style.transform =
        'translate(' + result.dx.toFixed(2) + '%, ' + result.dy.toFixed(2) + '%) scale(' +
        result.scale.toFixed(3) + ')';
      img.dataset.gfFeaturedSizing = result.scale.toFixed(3);
    }

    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);
            normalize(entry.target);
          });
        }, { rootMargin: '350px 0px' })
      : null;

    function register(root = document) {
      root.querySelectorAll(selector).forEach((img) => {
        if (img.dataset.gfFeaturedObserved === 'true') return;
        img.dataset.gfFeaturedObserved = 'true';
        if (io) io.observe(img);
        else normalize(img);
      });
    }

    register();

    const mo = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches && node.matches(selector)) register(node.parentElement || document);
          else register(node);
        }
      }
    });

    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
